import React, { useState, useCallback, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import {
  X as ClearIcon,
  ArrowUp,
  Plus,
  Loader,
  Clock4,
  Trash2,
} from 'lucide-react';
import { Square } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';

import { RootState } from '../../../../reducers';
import { MultiAskAIProps, Stage, LoadingStage } from './types';

import useLimits from '../../../plan/hooks/limits';
import {
  getMultiFileSessions,
  deleteMultiFileSession,
  askMultiFileQuestionStream,
} from '../../../../services/multiFileAskAIService';
import { screenAppAPIClient } from '../../../../lib/baseAxios';

import { useUserTags } from '../../../../hooks/useTags';
import { useOutputLanguage } from '../../../../hooks/useOutputLanguage';
import { FileType as GlobalFileType } from '../../../../types';
import Results from './Results';
import FileSelector from './FileSelector';

// Sanitize markdown to fix code block formatting
const sanitizeMarkdown = (markdown: string): string => {
  // Remove wrapping markdown code block if present
  let sanitized = markdown.replace(
    /^```\s*(?:markdown)?\n([\s\S]*?)\n```$/g,
    '$1',
  );
  // Remove any other code blocks
  sanitized = sanitized.replace(/```[\s\S]*?```/g, '');
  // Remove any inline code backticks
  sanitized = sanitized.replace(/`[^`]*`/g, '$1');
  // Remove any remaining backticks
  sanitized = sanitized.replace(/`/g, '');
  // Trim any extra whitespace that might have been left
  return sanitized.trim();
};

interface Props extends MultiAskAIProps {
  open?: boolean;
  onClose?: () => void;
}

// Saved conversation structure for history (matches backend SessionListItem)
interface SavedConversation {
  _id: string;
  fileIds: string[];
  conversation: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp?: string;
  }>;
  createdAt: string;
  updatedAt: string;
  teamId?: string;
}

const MultiAskAI: React.FC<Props> = ({ open = false }) => {
  const { t } = useTranslation();
  const [stage, setStage] = useState<Stage>('initial');
  const [loadingStage, setLoadingStage] = useState<LoadingStage>(null);
  const [cachedFiles, setCachedFiles] = useState<GlobalFileType[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showFileDropdown, setShowFileDropdown] = useState(false);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const [showSelectedFilesDropdown, setShowSelectedFilesDropdown] =
    useState(false);

  const team = useSelector((state: RootState) => state.team.selectedTeam);

  const guardLimits = useLimits();
  const [directions, setDirections] = useState('');
  const { getTag, setTag } = useUserTags();
  const [selectedFiles, setSelectedFiles] = useState<string[]>(() => {
    try {
      const savedFiles = getTag('multi-ask-ai-selected-files');
      return savedFiles ? JSON.parse(savedFiles as string) : [];
    } catch {
      return [];
    }
  });
  const { currentLanguageName } = useOutputLanguage();
  const [copySuccess, setCopySuccess] = useState(false);
  const [resultSearchTerm, setResultSearchTerm] = useState('');
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [savedConversations, setSavedConversations] = useState<
    SavedConversation[]
  >([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [completion, setCompletion] = useState<string>(
    getTag('multi-ask-ai-last-output') || '',
  );
  const [isLoading, setIsLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(
    getTag('multi-ask-ai-session-id') || null,
  );
  const [conversationHistory, setConversationHistory] = useState<
    Array<{ role: 'user' | 'assistant'; content: string }>
  >(() => {
    try {
      const savedHistory = getTag('multi-ask-ai-history');
      return savedHistory ? JSON.parse(savedHistory as string) : [];
    } catch {
      return [];
    }
  });

  // AbortController ref for stopping streaming responses
  const abortControllerRef = useRef<AbortController | null>(null);

  // Persist conversation history whenever it changes
  useEffect(() => {
    if (conversationHistory.length > 0) {
      setTag({
        key: 'multi-ask-ai-history',
        value: JSON.stringify(conversationHistory),
      });
    }
  }, [conversationHistory, setTag]);

  // Persist selected files whenever it changes
  useEffect(() => {
    if (selectedFiles.length > 0) {
      setTag({
        key: 'multi-ask-ai-selected-files',
        value: JSON.stringify(selectedFiles),
      });
    }
  }, [selectedFiles, setTag]);

  // Handle closing search input when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSearchInput && !resultSearchTerm) {
        const target = event.target as Element;
        if (!target.closest('[data-search-container]')) {
          setShowSearchInput(false);
        }
      }
    };

    if (showSearchInput) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [showSearchInput, resultSearchTerm]);

  // Handle closing file dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Check if click is outside dropdown AND not on a select dropdown
      if (
        !target.closest('[data-file-dropdown]') &&
        !target.closest('[role="combobox"]') &&
        !target.closest('[role="listbox"]')
      ) {
        setShowFileDropdown(false);
        // Focus the textarea when closing dropdown if files are selected
        if (selectedFiles.length > 0) {
          setTimeout(() => {
            textareaRef.current?.focus();
          }, 100);
        }
      }
    };

    if (showFileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [showFileDropdown, selectedFiles.length]);

  // Handle closing history dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-history-dropdown]')) {
        setShowHistoryDropdown(false);
      }
    };

    if (showHistoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [showHistoryDropdown]);

  // Handle closing selected files dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-selected-files-dropdown]')) {
        setShowSelectedFilesDropdown(false);
      }
    };

    if (showSelectedFilesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [showSelectedFilesDropdown]);

  const showToast = useCallback(
    (message: string, variant: 'default' | 'destructive' = 'default') => {
      if (variant === 'destructive') {
        toast.error(message);
      } else {
        toast(message);
      }
    },
    [],
  );

  // Stop the currently running generation
  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
      setLoadingStage(null);
      setStage('final');
    }
  }, []);

  // Load saved conversations from backend on mount
  useEffect(() => {
    const fetchSessions = async () => {
      if (!team?.teamId) return;

      setIsLoadingSessions(true);
      try {
        const response = await getMultiFileSessions(1, 50);
        if (response.success && response.data) {
          setSavedConversations(response.data.sessions);
        }
      } catch (error) {
        console.error('Failed to fetch conversation history:', error);
        showToast('Failed to load conversation history', 'destructive');
      } finally {
        setIsLoadingSessions(false);
      }
    };

    fetchSessions();
  }, [team?.teamId]);

  // Load files from current session on mount if there's an existing session
  useEffect(() => {
    if (!currentSessionId || savedConversations.length === 0) {
      console.log('Skipping file fetch - no session or no saved conversations');
      return;
    }

    // Find the current session in saved conversations
    const currentConversation = savedConversations.find(
      (conv) => conv._id === currentSessionId,
    );

    if (currentConversation && currentConversation.fileIds.length > 0) {
      // Restore the full conversation history from the backend
      setConversationHistory(currentConversation.conversation);

      // Find the last assistant response and set it as the completion
      const assistantMessage = currentConversation.conversation
        .slice()
        .reverse()
        .find((msg) => msg.role === 'assistant');

      if (assistantMessage) {
        setCompletion(assistantMessage.content);
      }

      // Set selected files
      setSelectedFiles(currentConversation.fileIds);
      setStage('final');

      // Fetch full file data for the conversation's files
      const fetchFilesData = async () => {
        try {
          const filePromises = currentConversation.fileIds.map(
            async (fileId) => {
              try {
                const response = await (screenAppAPIClient as any).api.get(
                  `/v2/files/${fileId}`,
                );
                return (
                  response.data?.data?.file ||
                  response.data?.file ||
                  response.data
                );
              } catch (error) {
                console.error(`Failed to fetch file ${fileId}:`, error);
                return null;
              }
            },
          );
          const filesData = await Promise.all(filePromises);
          const validFiles = filesData.filter(Boolean);
          console.log(
            'Fetched files data:',
            validFiles.map((f) => ({ id: f._id, name: f.name })),
          );
          setCachedFiles(validFiles);
        } catch (error) {
          console.error('Failed to fetch files data:', error);
          // Set empty cache on error
          setCachedFiles([]);
        }
      };
      fetchFilesData();
    }
  }, [currentSessionId, savedConversations]);

  // Fetch files whenever selectedFiles changes (fallback for when session ID is not available)
  useEffect(() => {
    if (selectedFiles.length === 0 || cachedFiles.length > 0) return;

    const fetchFilesData = async () => {
      try {
        const filePromises = selectedFiles.map(async (fileId) => {
          try {
            const response = await (screenAppAPIClient as any).api.get(
              `/v2/files/${fileId}`,
            );
            return (
              response.data?.data?.file || response.data?.file || response.data
            );
          } catch (error) {
            console.error(`Failed to fetch file ${fileId}:`, error);
            return null;
          }
        });
        const filesData = await Promise.all(filePromises);
        const validFiles = filesData.filter(Boolean);
        setCachedFiles(validFiles);
      } catch (error) {
        console.error('Failed to fetch files from selectedFiles:', error);
        setCachedFiles([]);
      }
    };

    fetchFilesData();
  }, [selectedFiles, cachedFiles.length]);

  // Refresh sessions list from backend
  const refreshSessions = useCallback(async () => {
    if (!team?.teamId) return;

    try {
      const response = await getMultiFileSessions(1, 50);
      if (response.success && response.data) {
        setSavedConversations(response.data.sessions);
      }
    } catch (error) {
      console.error('Failed to refresh conversation history:', error);
    }
  }, [team?.teamId]);

  // Note: Session tracking is handled by backend with the summarized endpoint
  // No need for client-side session management

  // Load a conversation from history
  const loadConversation = useCallback(
    (conversationId: string) => {
      const conversationToLoad = savedConversations.find(
        (conv) => conv._id === conversationId,
      );
      if (conversationToLoad) {
        // Restore the full conversation history
        setConversationHistory(conversationToLoad.conversation);

        // Restore the session ID for continuing the conversation
        setCurrentSessionId(conversationId);
        setTag({
          key: 'multi-ask-ai-session-id',
          value: conversationId,
        });

        // Find the last user message and assistant response
        const userMessage = conversationToLoad.conversation.find(
          (msg) => msg.role === 'user',
        );
        const assistantMessage = conversationToLoad.conversation
          .slice()
          .reverse()
          .find((msg) => msg.role === 'assistant');

        if (userMessage) {
          setDirections('');
        }

        if (assistantMessage) {
          // Backend now returns citations in correct markdown format [→](fileId@timestamp)
          setCompletion(assistantMessage.content);
        }

        setSelectedFiles(conversationToLoad.fileIds);
        setStage('final');

        // Fetch full file data for the conversation's files
        const fetchFilesData = async () => {
          try {
            const filePromises = conversationToLoad.fileIds.map(
              async (fileId) => {
                try {
                  const response = await (screenAppAPIClient as any).api.get(
                    `/v2/files/${fileId}`,
                  );
                  return (
                    response.data?.data?.file ||
                    response.data?.file ||
                    response.data
                  );
                } catch (error) {
                  console.error(`Failed to fetch file ${fileId}:`, error);
                  return null;
                }
              },
            );
            const filesData = await Promise.all(filePromises);
            const validFiles = filesData.filter(Boolean);
            setCachedFiles(validFiles);
          } catch (error) {
            console.error('Failed to fetch files data:', error);
            setCachedFiles([]);
          }
        };
        fetchFilesData();

        setShowHistoryDropdown(false);
      }
    },
    [savedConversations, setTag],
  );

  // Delete a conversation from history
  const deleteConversation = useCallback(
    async (conversationId: string, e?: React.MouseEvent) => {
      e?.stopPropagation();

      try {
        await deleteMultiFileSession(conversationId);
        // Remove from local state immediately for better UX
        setSavedConversations((prev) =>
          prev.filter((conv) => conv._id !== conversationId),
        );
        showToast('Session deleted successfully');
      } catch (error) {
        console.error('Failed to delete conversation:', error);
        showToast('Failed to delete session', 'destructive');
      }
    },
    [],
  );

  const runAskAI = async () => {
    if (selectedFiles.length === 0) {
      showToast(
        t(
          'drawer.multiAskAI.selectAtLeastOne',
          'Please select at least one file to analyze',
        ),
      );
      return;
    }

    try {
      await guardLimits('multipleRecordingAskAI', async () => {
        // Store the current directions before clearing
        const currentDirections = directions.trim();

        // Clear input immediately but keep file selection
        setDirections('');

        setStage('processing');
        setCompletion('');

        setLoadingStage('response');
        setIsLoading(true);

        try {
          if (!team?.teamId) {
            throw new Error('Team ID is required');
          }

          // Add user message to conversation history
          setConversationHistory((prev) => [
            ...prev,
            { role: 'user', content: currentDirections },
          ]);

          // Create AbortController for cancellation
          abortControllerRef.current = new AbortController();

          // Use streaming endpoint for real-time responses
          let fullResponse = '';
          const { sessionId } = await askMultiFileQuestionStream(
            team.teamId,
            {
              fileIds: selectedFiles,
              question: currentDirections,
              sessionId: currentSessionId ?? undefined,
            },
            (accumulatedText: string) => {
              // Service sends accumulated text, just display it
              fullResponse = accumulatedText;
              // Use flushSync to force immediate render for smooth streaming animation
              flushSync(() => {
                setCompletion(accumulatedText);
                // Transition to final stage as soon as we receive first chunk
                setStage('final');
              });
            },
            { signal: abortControllerRef.current.signal },
          );

          // Clear the abort controller after successful completion
          abortControllerRef.current = null;

          // Sanitize the complete response
          const sanitizedCompletion = sanitizeMarkdown(fullResponse);
          setCompletion(sanitizedCompletion);

          // Store the session ID for conversation continuity
          setCurrentSessionId(sessionId);
          setTag({
            key: 'multi-ask-ai-session-id',
            value: sessionId,
          });

          // Add assistant response to conversation history
          setConversationHistory((prev) => [
            ...prev,
            { role: 'assistant', content: sanitizedCompletion },
          ]);

          setStage('final');
          setTag({
            key: 'multi-ask-ai-last-output',
            value: sanitizedCompletion,
          });

          // Refresh sessions to show the new conversation
          await refreshSessions();
        } catch (error) {
          console.error('Failed to analyze recordings:', error);

          // Clear the abort controller
          abortControllerRef.current = null;

          // Check if error was due to abort
          if (error instanceof Error && error.name === 'AbortError') {
            // User cancelled - don't show error toast
            console.log('Request was cancelled by user');
          } else {
            setStage('initial');
            showToast(
              error instanceof Error
                ? error.message
                : t(
                    'drawer.multiAskAI.failedToProcessTranscripts',
                    'Failed to process transcripts. Please try again.',
                  ),
              'destructive',
            );
          }
        } finally {
          setLoadingStage(null);
          setIsLoading(false);
        }
      });
    } catch (error) {
      console.log('Guard limits reached:', error);
      setLoadingStage(null);
      setDirections('');
    }
  };

  const handleFileSelect = (file: GlobalFileType) => {
    const fileId = file._id;
    setSelectedFiles((prev) => {
      const newSelection = prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId];

      // Update cachedFiles based on the new selection
      if (prev.includes(fileId)) {
        // Remove from cache if deselected
        setCachedFiles((current) => current.filter((f) => f._id !== fileId));
        // Close the selected files dropdown if this was the last file
        if (prev.length === 1) {
          setShowSelectedFilesDropdown(false);
        }
      } else {
        // Add to cache if newly selected with full file object
        setCachedFiles((current) => [...current, file]);
        // Focus textarea when a file is selected
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 100);
      }

      return newSelection;
    });
  };

  const handleKeyPress = async (event: React.KeyboardEvent) => {
    if (
      event.key === 'Enter' &&
      stage !== 'processing' &&
      selectedFiles.length > 0 &&
      directions.trim() !== ''
    ) {
      event.preventDefault();
      runAskAI();
    }
  };

  const handleCopyContent = (cleanedText: string): void => {
    if (cleanedText) {
      navigator.clipboard.writeText(cleanedText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleEditQuery = async (messageIndex: number, newContent: string) => {
    // Find the user message at the given index
    const userMessage = conversationHistory[messageIndex];

    if (userMessage && userMessage.role === 'user') {
      // Truncate conversation history at this point (remove this message and all after)
      const truncatedHistory = conversationHistory.slice(0, messageIndex);
      setConversationHistory(truncatedHistory);

      // Update persisted history
      setTag({
        key: 'multi-ask-ai-history',
        value: JSON.stringify(truncatedHistory),
      });

      // Clear the completion to show we're processing
      setCompletion('');

      // Set loading state
      setStage('processing');
      setLoadingStage('response');
      setIsLoading(true);

      try {
        if (!team?.teamId) {
          throw new Error('Team ID is required');
        }

        // Add the edited user message to conversation history
        setConversationHistory((prev) => [
          ...prev,
          { role: 'user', content: newContent },
        ]);

        // Create AbortController for cancellation
        abortControllerRef.current = new AbortController();

        // Use streaming endpoint for real-time responses
        let fullResponse = '';
        const { sessionId } = await askMultiFileQuestionStream(
          team.teamId,
          {
            fileIds: selectedFiles,
            question: newContent,
            sessionId: currentSessionId ?? undefined,
          },
          (accumulatedText: string) => {
            // Service sends accumulated text, just display it
            fullResponse = accumulatedText;
            // Use flushSync to force immediate render for smooth streaming animation
            flushSync(() => {
              setCompletion(accumulatedText);
              // Transition to final stage as soon as we receive first chunk
              setStage('final');
            });
          },
          { signal: abortControllerRef.current.signal },
        );

        // Clear the abort controller after successful completion
        abortControllerRef.current = null;

        // Sanitize the complete response
        const sanitizedCompletion = sanitizeMarkdown(fullResponse);
        setCompletion(sanitizedCompletion);

        // Store the session ID for conversation continuity
        setCurrentSessionId(sessionId);
        setTag({
          key: 'multi-ask-ai-session-id',
          value: sessionId,
        });

        // Add assistant response to conversation history
        setConversationHistory((prev) => [
          ...prev,
          { role: 'assistant', content: sanitizedCompletion },
        ]);

        setStage('final');
        setTag({
          key: 'multi-ask-ai-last-output',
          value: sanitizedCompletion,
        });

        // Refresh sessions to show the updated conversation
        await refreshSessions();
      } catch (error) {
        console.error('Failed to analyze recordings:', error);

        // Clear the abort controller
        abortControllerRef.current = null;

        // Check if error was due to abort
        if (error instanceof Error && error.name === 'AbortError') {
          // User cancelled - don't show error toast
          console.log('Request was cancelled by user');
        } else {
          setStage('initial');
          showToast(
            error instanceof Error
              ? error.message
              : t(
                  'drawer.multiAskAI.failedToProcessTranscripts',
                  'Failed to process transcripts. Please try again.',
                ),
            'destructive',
          );
        }
      } finally {
        setLoadingStage(null);
        setIsLoading(false);
      }
    }
  };

  const handleRegenerateResponse = async (messageIndex: number) => {
    // Find the assistant message at the given index
    const assistantMessage = conversationHistory[messageIndex];

    if (assistantMessage && assistantMessage.role === 'assistant') {
      // Find the corresponding user message (should be right before this assistant message)
      const userMessageIndex = messageIndex - 1;
      const userMessage = conversationHistory[userMessageIndex];

      if (userMessage && userMessage.role === 'user') {
        // Truncate conversation history at the assistant message (keeping the user message)
        const truncatedHistory = conversationHistory.slice(0, messageIndex);

        // Use flushSync to ensure state updates happen immediately before streaming starts
        flushSync(() => {
          setConversationHistory(truncatedHistory);
          // Clear the completion to show we're processing
          setCompletion('');
          // Set loading state
          setStage('processing');
          setLoadingStage('response');
          setIsLoading(true);
        });

        // Update persisted history
        setTag({
          key: 'multi-ask-ai-history',
          value: JSON.stringify(truncatedHistory),
        });

        try {
          if (!team?.teamId) {
            throw new Error('Team ID is required');
          }

          // Create AbortController for cancellation
          abortControllerRef.current = new AbortController();

          // Use streaming endpoint with the original user message
          let fullResponse = '';
          const { sessionId } = await askMultiFileQuestionStream(
            team.teamId,
            {
              fileIds: selectedFiles,
              question: userMessage.content,
              sessionId: currentSessionId ?? undefined,
            },
            (accumulatedText: string) => {
              // Service sends accumulated text, just display it
              fullResponse = accumulatedText;
              // Use flushSync to force immediate render for smooth streaming animation
              flushSync(() => {
                setCompletion(accumulatedText);
                // Transition to final stage as soon as we receive first chunk
                setStage('final');
              });
            },
            { signal: abortControllerRef.current.signal },
          );

          // Clear the abort controller after successful completion
          abortControllerRef.current = null;

          // Sanitize the complete response
          const sanitizedCompletion = sanitizeMarkdown(fullResponse);
          setCompletion(sanitizedCompletion);

          // Store the session ID for conversation continuity
          setCurrentSessionId(sessionId);
          setTag({
            key: 'multi-ask-ai-session-id',
            value: sessionId,
          });

          // Add new assistant response to conversation history
          setConversationHistory((prev) => [
            ...prev,
            { role: 'assistant', content: sanitizedCompletion },
          ]);

          setStage('final');
          setTag({
            key: 'multi-ask-ai-last-output',
            value: sanitizedCompletion,
          });

          // Refresh sessions to show the updated conversation
          await refreshSessions();
        } catch (error) {
          console.error('Failed to regenerate response:', error);

          // Clear the abort controller
          abortControllerRef.current = null;

          // Check if error was due to abort
          if (error instanceof Error && error.name === 'AbortError') {
            // User cancelled - don't show error toast
            console.log('Request was cancelled by user');
          } else {
            setStage('initial');
            showToast(
              error instanceof Error
                ? error.message
                : t(
                    'drawer.multiAskAI.failedToProcessTranscripts',
                    'Failed to regenerate response. Please try again.',
                  ),
              'destructive',
            );
          }
        } finally {
          setLoadingStage(null);
          setIsLoading(false);
        }
      }
    }
  };

  const renderInputArea = () => {
    return (
      <div className="p-4">
        <div className="max-w-3xl mx-auto w-full">
          {/* Single rounded container for everything */}
          <div
            className={cn(
              'relative rounded-3xl bg-white dark:bg-background overflow-visible transition-all duration-300 ease-in-out border shadow-sm',
              isFocused
                ? 'border-primary shadow-md'
                : 'border-border shadow-sm',
            )}
          >
            {/* Input area with textarea and controls */}
            <div className="relative">
              <Textarea
                ref={textareaRef}
                placeholder={
                  selectedFiles.length === 0
                    ? t(
                        'drawer.multiAskAI.searchPlaceholder.noFiles',
                        'Select files and Ask Anything',
                      )
                    : t(
                        'drawer.multiAskAI.searchPlaceholder.withFiles',
                        `Ask anything about these recordings... (Output: ${currentLanguageName})`,
                      )
                }
                value={directions}
                onChange={(e) => {
                  setDirections(e.target.value);
                  // Auto-resize textarea
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onKeyPress={handleKeyPress}
                onFocus={(e) => {
                  setIsFocused(true);
                  // Ensure proper height on focus
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onBlur={() => setIsFocused(false)}
                disabled={stage === 'processing'}
                className={cn(
                  'w-full pl-4 pr-12 pt-3 pb-12 min-h-[80px] max-h-[200px] resize-none overflow-y-auto border-0 bg-transparent focus:ring-0 focus:outline-none focus:border-0 focus-visible:ring-0 focus-visible:ring-offset-0',
                )}
                style={{ height: 'auto' }}
              />

              {/* Bottom row with plus button, history button, and file chips */}
              <div
                className="absolute bottom-2 left-2 right-12 flex items-center gap-2"
                data-file-dropdown
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          // Close history dropdown if it's open
                          if (showHistoryDropdown) {
                            setShowHistoryDropdown(false);
                          }
                          setShowFileDropdown(!showFileDropdown);
                          // Focus textarea when closing dropdown if files are selected
                          if (showFileDropdown && selectedFiles.length > 0) {
                            setTimeout(() => {
                              textareaRef.current?.focus();
                            }, 100);
                          }
                        }}
                        className={cn(
                          'h-8 w-8 rounded-full flex-shrink-0 transition-all',
                          selectedFiles.length === 0
                            ? 'bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground'
                            : 'bg-secondary/50 hover:bg-secondary/80 text-secondary-foreground/70',
                        )}
                      >
                        <Plus className="h-4 w-4 transition-colors" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Add recordings</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* History button - always visible */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          // Close file dropdown if it's open
                          if (showFileDropdown) {
                            setShowFileDropdown(false);
                          }
                          setShowHistoryDropdown(!showHistoryDropdown);
                        }}
                        className="h-8 w-8 rounded-full flex-shrink-0 bg-secondary/50 hover:bg-secondary/80 text-secondary-foreground/70"
                      >
                        <Clock4 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View history</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Single file chip showing count - clicking opens dropdown */}
                {selectedFiles.length > 0 && (
                  <Badge
                    variant="secondary"
                    onClick={() =>
                      setShowSelectedFilesDropdown(!showSelectedFilesDropdown)
                    }
                    className="h-8 bg-secondary/50 hover:bg-secondary/80 text-secondary-foreground/70 px-3 border-0 rounded-full cursor-pointer flex items-center gap-1.5 flex-shrink-0 font-normal"
                  >
                    <span className="truncate text-xs max-w-[150px]">
                      {cachedFiles[0]?.name || 'Files'}
                    </span>
                    {selectedFiles.length > 1 && (
                      <span className="text-xs">
                        +{selectedFiles.length - 1} more
                      </span>
                    )}
                  </Badge>
                )}

                {/* File Dropdown menu - positioned above the button */}
                {showFileDropdown && (
                  <FileSelector
                    selectedFiles={selectedFiles}
                    onFileSelect={handleFileSelect}
                  />
                )}

                {/* History Dropdown menu - positioned above the button */}
                {showHistoryDropdown && (
                  <Card
                    className="absolute bottom-full mb-2 left-10 z-50 w-96 max-h-96 overflow-hidden"
                    data-history-dropdown
                  >
                    {/* Header section matching the + menu style */}
                    <div className="p-4 border-b">
                      <h3 className="text-sm font-medium">
                        {t(
                          'drawer.multiAskAI.recentSessions',
                          'Recent Sessions',
                        )}
                      </h3>
                    </div>

                    {/* Content area in ScrollArea */}
                    <ScrollArea className="h-64">
                      {(() => {
                        if (isLoadingSessions) {
                          return (
                            <div className="flex items-center justify-center py-8">
                              <Loader className="animate-spin h-5 w-5 text-primary" />
                            </div>
                          );
                        }

                        if (savedConversations.length === 0) {
                          return (
                            <div className="flex items-center justify-center py-16 px-8">
                              <p className="text-sm text-muted-foreground text-center">
                                {t(
                                  'drawer.multiAskAI.noHistory',
                                  'No history available yet',
                                )}
                              </p>
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-0">
                            {savedConversations.map((conv) => {
                              // Get the first user message as the title
                              const firstUserMsg = conv.conversation.find(
                                (msg) => msg.role === 'user',
                              );
                              const title =
                                firstUserMsg?.content.slice(0, 50) ||
                                'Untitled';

                              const timeAgo = (() => {
                                const diff =
                                  Date.now() -
                                  new Date(conv.updatedAt).getTime();
                                const minutes = Math.floor(diff / 60000);
                                const hours = Math.floor(diff / 3600000);
                                const days = Math.floor(diff / 86400000);

                                if (minutes < 1) return 'Just now';
                                if (minutes < 60) return `${minutes}m ago`;
                                if (hours < 24) return `${hours}h ago`;
                                return `${days}d ago`;
                              })();

                              return (
                                <Card
                                  key={conv._id}
                                  className={cn(
                                    'border-0 rounded-none border-b last:border-b-0 shadow-none transition-colors',
                                  )}
                                >
                                  <CardContent
                                    className="group flex items-center justify-between gap-3 p-3 cursor-pointer hover:bg-accent/50 transition-colors"
                                    onClick={() => loadConversation(conv._id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        loadConversation(conv._id);
                                      }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm mb-1 line-clamp-1 font-normal">
                                        {title}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <div className="text-xs text-muted-foreground">
                                          {conv.fileIds.length}{' '}
                                          {conv.fileIds.length === 1
                                            ? 'file'
                                            : 'files'}
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                          •
                                        </span>
                                        <div className="text-xs text-muted-foreground">
                                          {timeAgo}
                                        </div>
                                      </div>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) =>
                                        deleteConversation(conv._id, e)
                                      }
                                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                    >
                                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                    </Button>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </ScrollArea>
                  </Card>
                )}

                {/* Selected Files Dropdown menu - positioned above the file chip */}
                {showSelectedFilesDropdown && (
                  <Card
                    className="absolute bottom-full mb-2 left-20 z-50 w-80 max-h-80 overflow-hidden"
                    data-selected-files-dropdown
                  >
                    {/* Content area in ScrollArea */}
                    <ScrollArea className="max-h-64">
                      <div className="space-y-0">
                        {cachedFiles.map((file) => (
                          <Card
                            key={file._id}
                            className="border-0 rounded-none border-b last:border-b-0 shadow-none transition-colors"
                          >
                            <CardContent className="group flex items-center justify-between gap-3 p-3 hover:bg-accent/50 transition-colors">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm line-clamp-1 font-normal">
                                  {file.name}
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFileSelect(file);
                                }}
                                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                              >
                                <ClearIcon className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                              </Button>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </ScrollArea>
                  </Card>
                )}
              </div>

              {/* Send/Stop button in bottom right */}
              <div className="absolute bottom-2 right-2">
                {isLoading ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          onClick={stopGeneration}
                          size="icon"
                          variant="default"
                          className="h-8 w-8 rounded-full transition-all shadow-sm hover:shadow-md"
                          style={{ borderRadius: '50%' }}
                        >
                          <Square
                            className="w-4 h-4 text-white"
                            weight="fill"
                          />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Stop generating</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          onClick={runAskAI}
                          disabled={
                            !directions ||
                            selectedFiles.length === 0 ||
                            stage === 'processing'
                          }
                          size="icon"
                          variant={
                            !directions || selectedFiles.length === 0
                              ? 'ghost'
                              : 'default'
                          }
                          className={cn(
                            'group h-8 w-8 rounded-full transition-all',
                            !directions || selectedFiles.length === 0
                              ? 'bg-muted hover:bg-muted cursor-not-allowed'
                              : 'shadow-sm hover:shadow-md',
                          )}
                          style={{ borderRadius: '50%' }}
                        >
                          <ArrowUp
                            className={cn(
                              'w-4 h-4 transition-transform',
                              !directions || selectedFiles.length === 0
                                ? 'text-muted-foreground'
                                : 'text-white',
                              directions &&
                                selectedFiles.length > 0 &&
                                'group-hover:translate-y-[-1px]',
                            )}
                          />
                        </Button>
                      </TooltipTrigger>
                      {(!directions || selectedFiles.length === 0) && (
                        <TooltipContent>
                          {selectedFiles.length === 0
                            ? 'Select recordings to analyze'
                            : 'Enter a question to ask'}
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Calculate if we should show centered state
  const isCenteredState =
    stage === 'initial' && !completion && conversationHistory.length === 0;

  return open ? (
    <div className="h-full w-full relative overflow-hidden">
      {/* Results Area - Using Collapsible for smooth expansion */}
      <div className="absolute inset-x-0 top-0 bottom-[50px]">
        <Collapsible open={!isCenteredState} className="h-full">
          <CollapsibleContent className="h-full overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.4,0.0,0.2,1)] data-[state=closed]:animate-[collapsible-up_500ms_cubic-bezier(0.4,0.0,0.2,1)] data-[state=open]:animate-[collapsible-down_500ms_cubic-bezier(0.4,0.0,0.2,1)]">
            <Results
              completion={completion}
              isLoading={isLoading}
              stage={stage}
              loadingStage={loadingStage}
              files={{ data: cachedFiles }}
              hideReferences={false}
              resultSearchTerm={resultSearchTerm}
              setResultSearchTerm={setResultSearchTerm}
              onCopyContent={handleCopyContent}
              copySuccess={copySuccess}
              conversationHistory={conversationHistory}
              hideToolbar={isCenteredState}
              onClearCompletion={() => {
                setCompletion('');
                setStage('initial');
                setDirections('');
                setSelectedFiles([]);
                setCachedFiles([]);
                setCurrentSessionId(null);
                setConversationHistory([]);
                setTag({
                  key: 'multi-ask-ai-last-output',
                  value: '',
                });
                setTag({
                  key: 'multi-ask-ai-session-id',
                  value: '',
                });
                setTag({
                  key: 'multi-ask-ai-history',
                  value: '',
                });
              }}
              onEditQuery={handleEditQuery}
              onRegenerateResponse={handleRegenerateResponse}
            />
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* Input Area - Animated between center and bottom */}
      <div
        className={cn(
          'absolute inset-x-0 transition-all duration-500 ease-[cubic-bezier(0.4,0.0,0.2,1)]',
          isCenteredState
            ? 'top-1/2 -translate-y-1/2'
            : 'bottom-0 translate-y-0',
        )}
      >
        <div
          className={cn('w-full', isCenteredState ? 'max-w-3xl mx-auto' : '')}
        >
          {renderInputArea()}
        </div>
      </div>
    </div>
  ) : null;
};

export default MultiAskAI;

// Page wrapper component for use in routes
export const MultiAskAIPage: React.FC = () => {
  const [isOpen, setIsOpen] = useState(true);

  const handleClose = () => {
    setIsOpen(false);
    // Navigate back to previous page after closing
    window.history.back();
  };

  return (
    <div className="flex w-[calc(100%-32px)] h-[calc(100vh-64px-16px)] m-[8px_16px_8px_16px] mt-0 relative top-0 bg-background rounded-2xl overflow-hidden">
      <MultiAskAI open={isOpen} onClose={handleClose} />
    </div>
  );
};
